import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { BasicModal, SelectInput, ToastType } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { closeModal } from '@store/modals';
import {
  getInitialStates,
  getStateDefinition,
} from '@store/orderFlagStates/store';
import {
  addFlagCommentV4,
  CommentViewModel,
  deleteFlagComment,
  DisputedOrderFlagT,
  disputeFlag,
  FlagType,
  orderStore,
  setFlagComments,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { userStore } from '@store/user';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import {
  CheckIcon,
  CancelIcon,
  InformationOutlineIcon,
  CogIcon,
} from '@atgfr8/icons';
import { FlagViewModel } from '@typeDefinitions/flagTypes';
import { handleToast, isAdmin } from '@utils/utils';
import { batch, createMemo, createSignal, Show } from 'solid-js';
import { unwrap } from 'solid-js/store';
import { effect } from 'solid-js/web';

import cls from './classes';
import {
  flagImgs,
  flagTypesWithMessaging,
  getOptionsByRole,
  infoMsgObj,
  statefulFlagErrorMessages,
} from './constants';
import { LoadComment } from './LoadComment';
import { SpecialFlagTypeEnum } from './types';

type PropsT = {
  modalId: string;
  flag?: FlagViewModel;
  isLTL?: boolean;
  mode?: string;
  onClose?: () => void;
  onChange?: (
    flag: FlagViewModel,
    action: 'update' | 'create' | 'delete',
  ) => void;
  onFlagTypeChange?: (flagType: FlagType) => void;
  onDispute?: (flag: FlagViewModel) => void;
  changeTrackingSource?: ChangeTrackingSource;
  changeTrackingSources?: Partial<
    Record<
      | 'addComment'
      | 'create'
      | 'delete'
      | 'deleteComment'
      | 'dispute'
      | 'update',
      ChangeTrackingSource
    >
  >;
  loading?: boolean;
};

/**
 * The new flag popup is a reusable component. Make sure that the `onClose`
 * and `onChange` handle every state change, such as resetting or changing
 * the `flag` to a different value.
 */
// eslint-disable-next-line complexity
export const FlagPopup = (props: PropsT) => {
  const isOrderFlagStateMachineEnabled = isFeatureFlagEnabled(
    FeatureFlags.feature_tms_orderflag_state_machine,
  );

  const [selectedFlag, setSelectedFlag] = createSignal(
    props.flag ?? {
      description: '',
      flagType: FlagType.None,
    },
  );
  const [isModified, setIsModified] = createSignal(false);
  const [isNeedsApproval, setNeedsApproval] = createSignal(
    props.flag !== undefined &&
      props.flag.id !== undefined &&
      selectedFlag().flagType === FlagType.NeedsApproval,
  );
  const [message, setMessage] = createSignal('');
  const [disputedOrderFlags, setDisputedOrderFlags] = createSignal<
    DisputedOrderFlagT[]
  >(props.flag?.disputedOrderFlags ?? []);
  const [comments, setComments] = createSignal(props.flag?.comments ?? []);

  const [selectedState, setSelectedState] = createSignal(
    props.flag?.currentState ?? '',
  );

  const currentFlagType = createMemo(() => selectedFlag().flagType);
  const getChangeTrackingSource = (
    action:
      | 'addComment'
      | 'create'
      | 'delete'
      | 'deleteComment'
      | 'dispute'
      | 'update',
  ) => props.changeTrackingSources?.[action] ?? props.changeTrackingSource;

  const stateDefinition = createMemo(() =>
    getStateDefinition(currentFlagType(), props.mode ?? ''),
  );

  const availableStates = createMemo(() =>
    getInitialStates(currentFlagType(), props.mode ?? ''),
  );

  const stateMenuItems = createMemo(() =>
    availableStates().map((state) => ({
      label: state,
      value: state,
    })),
  );
  const getStateRequiredErrorMessage = (flagType: string): string => {
    return statefulFlagErrorMessages[flagType] ?? '';
  };
  const stateErrorMessage = createMemo(() =>
    getStateRequiredErrorMessage(currentFlagType()),
  );
  const showStateError = createMemo(
    () => isOrderFlagStateMachineEnabled() && stateErrorMessage() !== '',
  );

  effect(() => {
    setNeedsApproval(
      props.flag !== undefined &&
        props.flag.id !== undefined &&
        selectedFlag().flagType === FlagType.NeedsApproval,
    );
    setComments(props.flag?.comments ?? []);

    if (isModified()) {
      return;
    }

    setDisputedOrderFlags(props.flag?.disputedOrderFlags ?? []);

    if (
      (props.flag !== undefined &&
        (props.flag.flagType !== selectedFlag().flagType ||
          props.flag.description !== selectedFlag().description ||
          props.flag.id !== selectedFlag().id)) ||
      props.flag === undefined
    ) {
      setSelectedFlag(
        props.flag ?? {
          description: '',
          flagType: FlagType.None,
        },
      );
      setSelectedState(props.flag?.currentState ?? '');
    }
  });

  const onCloseModal = () => {
    const flag = props.flag;
    const currentComments = comments();

    setIsModified(false);
    setMessage('');
    setNeedsApproval(false);
    closeModal(props.modalId);
    props.onClose?.();

    const loadIdx = orderStore.order.loads.findIndex(
      (l) => l.id === (flag?.loadId ?? 0),
    );

    if (loadIdx >= 0) {
      const newLoads = [...orderStore.order.loads];
      const newFlags = newLoads[loadIdx].flags ?? [];
      const flagIdx = newFlags.findIndex((f) => f.id === flag?.id);

      setFlagComments(loadIdx, flagIdx, currentComments);
    }
  };

  const onCloseOrDelete = () => {
    if (selectedFlag().id !== undefined) {
      props.onChange?.(selectedFlag(), 'delete');
    } else {
      onCloseModal();
    }
  };

  const onNeedsApprovalAction = (stillWorking = false) => {
    if (stillWorking) {
      onCloseModal();
    } else {
      onCloseOrDelete();
    }
  };

  const isDisputable = () => {
    if (props.flag === undefined) {
      return false;
    }

    if (props.flag.cleared ?? false) {
      return false;
    } else if (disputedOrderFlags().length === 0) {
      return true;
    }

    return disputedOrderFlags().some((flag) => {
      return flag.clearedDate !== undefined && flag.clearedDate !== null;
    });
  };

  const onDisputed = async () => {
    try {
      const res = await disputeFlag(
        props.flag?.id ?? 0,
        userStore.user.name,
        getChangeTrackingSource('dispute'),
      );

      if (res) {
        const newComment = res;
        setDisputedOrderFlags((prev) => {
          const flags = unwrap(prev);

          return [
            ...flags,
            {
              clearedBy: undefined,
              clearedDate: undefined,
              disputedDate:
                newComment.timestamp?.toString() ?? new Date().toISOString(),
              flagId: props.flag?.id ?? 0,
              id: '',
              operationType: 'None',
            },
          ];
        });

        setComments((prevComments) => {
          return [
            {
              operationType: res.operationType,
              id: res.id,
              objectId: res.objectId,
              userId: res.userId,
              name: res.name,
              type: res.type,
              comment: res.comment,
              timestamp: res.timestamp,
              thumbnail: res.thumbnail,
              createdBy: res.createdBy,
            },
            ...prevComments,
          ];
        });

        const updatedFlag: FlagViewModel = {
          ...selectedFlag(),
          comments: comments(),
          commentCount: comments().length,
          disputedOrderFlags: disputedOrderFlags(),
        };

        props.onDispute?.(updatedFlag);

        onCloseModal();
      }
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    }
  };

  const handleAddComment = async (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!message().trim() || message().length > 2000) {
        return;
      }

      const comment = (await addFlagCommentV4(
        props.flag?.id ?? 0,
        message(),
        getChangeTrackingSource('addComment'),
      )) as unknown as CommentViewModel;

      const updatedFlags = [
        ...(orderStore.order.loads[orderStore.activeTab.index]?.flags ?? []),
      ].map((flag) => {
        if (flag.id === props.flag?.id) {
          return {
            ...flag,
            comments: [...(flag.comments || []), comment],
          };
        }

        return flag;
      });

      updateLoadPropertyAtIndex({ flags: updatedFlags });
      setComments((prev) => {
        return [...prev, comment];
      });
      setMessage('');
    }
  };

  return (
    <BasicModal
      id={props.modalId}
      title="Add Flag"
      footer={false}
      onClose={onCloseModal}
      width="50vw"
      maxWidth="50vw"
      modalStyles={{
        background: 'white',
        borderRadius: '5px',
      }}
    >
      <Box>
        {isNeedsApproval() ? (
          <Box class={cls.headerPopup} mb="10px">
            <InformationOutlineIcon class="text-#0070A2-700 mr-2 inline-block flex-0.2" />
            <span
              class={cls.flagType}
              innerHTML={infoMsgObj[selectedFlag().flagType]}
            ></span>
          </Box>
        ) : undefined}
        <Box class="flex justify-between">
          <Box class="flex-[0.75]! mt-2">
            <SelectInput
              label="Flag Type *"
              menuItems={getOptionsByRole(
                isAdmin(),
                Boolean(orderStore.order.needsApprovalFlag),
              )}
              value={selectedFlag().flagType}
              onChange={(value) => {
                setIsModified(true);
                const newFlagType = value as FlagType;
                setSelectedState('');
                setSelectedFlag({
                  ...selectedFlag(),
                  flagType: newFlagType,
                });
                props.onFlagTypeChange?.(newFlagType);
              }}
              dataTestId="flag-popup-flag-type-select"
            />
            {(selectedFlag().flagType !== FlagType.None &&
              (props.flag === undefined || props.flag.id === undefined)) ||
            (props.flag !== undefined &&
              props.flag.id !== undefined &&
              selectedFlag().flagType !== FlagType.NeedsApproval) ? (
              <div class={cls.infoOutlinedIcon}>
                <InformationOutlineIcon class={cls.infoOutlinedIconClass} />
                <span
                  class={cls.flagType}
                  innerHTML={infoMsgObj[selectedFlag().flagType]}
                ></span>
              </div>
            ) : undefined}
            {/* Dynamic state dropdown - shows when flag type has state definitions */}
            <Show
              when={
                isOrderFlagStateMachineEnabled() &&
                stateDefinition() &&
                stateMenuItems().length > 0
              }
            >
              <Box mt="10px">
                <SelectInput
                  label={`${stateDefinition()?.stateName ?? 'State'}`}
                  menuItems={stateMenuItems()}
                  value={selectedState()}
                  onChange={(value) => {
                    setIsModified(true);
                    setSelectedState(value);
                  }}
                  dataTestId="flag-popup-state-select"
                />
              </Box>
              <Show when={!selectedState() && showStateError()}>
                <Box class="text-red-500 text-sm mt-1">
                  {stateErrorMessage()}
                </Box>
              </Show>
            </Show>
          </Box>
          <Box>
            <Box
              class={`w-[100px] h-[${isNeedsApproval() ? 56 : 100}px] ${
                isNeedsApproval() ? '' : 'border border-gray-300 p-1 py-2'
              } rounded flex items-center justify-center`}
            >
              {(selectedFlag().flagType !== FlagType.None &&
                (props.flag === undefined || props.flag.id === undefined)) ||
              (props.flag !== undefined &&
                props.flag.id !== undefined &&
                selectedFlag().flagType !== FlagType.NeedsApproval) ? (
                <img src={flagImgs[selectedFlag().flagType]} alt="alt flag" />
              ) : undefined}
            </Box>
          </Box>
        </Box>
        {selectedFlag().flagType === FlagType.MissingPaperwork ? (
          <Box marginTop="20px">
            <TextField
              label="Email ID *"
              dataTestId={'add-email-address-textInput'}
              placeholder="Add an email address"
              value={selectedFlag().emailId}
              class="w-full"
              onChange={(emailId: string) => {
                setSelectedFlag((prev) => {
                  return {
                    ...prev,
                    emailId,
                  };
                });
              }}
            />
          </Box>
        ) : undefined}
        <Box marginTop="20px">
          <TextField
            multiline={true}
            dataTestId={'description-textInput'}
            rows={isNeedsApproval() ? 2 : 5}
            value={selectedFlag().description}
            label="Description"
            placeholder={'Enter Description here'}
            class="w-full"
            error={
              (selectedFlag().description?.length ?? 0) > 4000
                ? 'Description should not be greater than 4000 characters.'
                : undefined
            }
            onChange={(value: string) => {
              batch(() => {
                setIsModified(true);
                setSelectedFlag({
                  ...selectedFlag(),
                  description: value,
                });
              });
            }}
          />
        </Box>
        {props.flag !== undefined &&
        flagTypesWithMessaging[
          props.flag.flagType as unknown as SpecialFlagTypeEnum
        ] ? (
          <Box mt="18px">
            <Box>
              <Typography
                class="text-black text-xl! font-medium!  leading-8! tracking-[0.15px]!"
                variant="h6"
                component="h2"
                sxProps={{ marginBottom: '10px' }}
              >
                Message History
              </Typography>
              <TextField
                value={message()}
                dataTestId={'add-comment-textInput'}
                label="Add a Comment"
                class="w-full"
                error={
                  message().length > 2000
                    ? 'Comment should not exceed 2000 characters.'
                    : undefined
                }
                helperText={`${message().length}/2000`}
                onChange={(value: string) => {
                  setMessage(value);
                }}
                onKeyDown={(e: KeyboardEvent) => {
                  void handleAddComment(e);
                }}
              />
            </Box>
            <Box maxHeight="250px" overflow="auto" marginTop="10px">
              <Stack direction="column">
                {comments()
                  .toReversed()
                  .map((comment) => {
                    if (!Boolean(comment)) {
                      return <></>;
                    }

                    return (
                      <LoadComment
                        comment={comment}
                        onDelete={async () => {
                          const res = (await deleteFlagComment(
                            comment.id,
                            getChangeTrackingSource('deleteComment'),
                          )) as unknown as {
                            isSuccess: boolean;
                          };

                          if (res.isSuccess) {
                            const idx = comments().findIndex(
                              (c) => c.id === comment.id,
                            );
                            setComments((prev) => {
                              const newComments = [...prev];
                              newComments.splice(idx, 1);
                              return newComments;
                            });
                          }
                        }}
                      />
                    );
                  })}
              </Stack>
            </Box>
          </Box>
        ) : undefined}
        <Box class="flex justify-end mt-14" gap={3}>
          {isNeedsApproval() ? (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => onNeedsApprovalAction()}
                dataTestId="flagpopup-ok-to-process"
              >
                <CheckIcon size={20} class={cls.iconSpacing} />
                Ok To Process
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => onNeedsApprovalAction(true)}
                dataTestId="flagpopup-still-working"
              >
                <CogIcon size={20} class={cls.iconSpacing} />
                Still Working
              </Button>
              {(props.isLTL ?? false) ? (
                <Button
                  variant="contained"
                  color="warning"
                  disabled={!isDisputable()}
                  onClick={onDisputed}
                  dataTestId="flagpopup-dispute"
                >
                  <CancelIcon size={20} class={cls.iconSpacing} />
                  Dispute
                </Button>
              ) : undefined}
            </>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={() => {
                  const action =
                    selectedFlag().id !== undefined ? 'update' : 'create';
                  const flagToSave = stateDefinition()
                    ? { ...selectedFlag(), currentState: selectedState() }
                    : selectedFlag();
                  props.onChange?.(flagToSave, action);
                  setIsModified(false);
                }}
                disabled={
                  selectedFlag().flagType === FlagType.None ||
                  (selectedFlag().description?.length ?? 0) > 4000 ||
                  (props.loading !== undefined && props.loading) ||
                  (selectedFlag().flagType === FlagType.MissingPaperwork &&
                    (selectedFlag().emailId === undefined ||
                      selectedFlag().emailId === '')) ||
                  (showStateError() && !selectedState())
                }
                dataTestId={`flagpopup-${selectedFlag().id ? 'update' : 'create'}-flag`}
              >
                {selectedFlag().id !== undefined
                  ? 'Update Flag'
                  : 'Create Flag'}
              </Button>
              {/* (Editable && !IsNew) || FlagType == '@(Globals.OrderFlags.BillingNote)' || FlagType == '@(Globals.OrderFlags.BillingHold)' */}
              {(selectedFlag().editable ?? false) &&
              props.flag !== undefined ? (
                <Button
                  onClick={onCloseOrDelete}
                  variant="contained"
                  color="error"
                  dataTestId={`flagpopup-${selectedFlag().id ? 'remove-flag' : 'cancel'}`}
                >
                  {selectedFlag().id !== undefined ? 'Remove Flag' : 'Cancel'}
                </Button>
              ) : undefined}
            </>
          )}
          <Button
            onClick={onCloseModal}
            variant="text"
            dataTestId="flagpopup-abort"
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </BasicModal>
  );
};
